/**
 * FavoritesScreen.web.tsx - Luxe favorieten pagina
 * Scrollen door je favorieten + aanraders
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, ScrollView, Animated, Easing } from 'react-native';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { productService } from '../services/ProductService';
// @ts-ignore
import { favoritesService } from '../services/FavoritesService';
import type { ProductDisplay } from '../models';

/* -------------------------------------------------------------------------- */
/*                         AANBEVOLEN PRODUCT KAARTJE                         */
/* -------------------------------------------------------------------------- */

interface AanbevolenKaartEigenschappen {
  product: ProductDisplay;
  bijKlik: () => void;
  bijToevoegenFavoriet: () => void;
  isFavoriet: boolean;
}

const AanbevolenKaart: React.FC<AanbevolenKaartEigenschappen> = ({ product, bijKlik, bijToevoegenFavoriet, isFavoriet }) => {
  const schaalAnimatie = useRef(new Animated.Value(1)).current;
  const [imgError, setImgError] = useState(false);

  const behandelToevoegKlik = () => {
    Animated.sequence([
      Animated.timing(schaalAnimatie, { toValue: 1.2, duration: 100, useNativeDriver: false }),
      Animated.timing(schaalAnimatie, { toValue: 1, duration: 100, useNativeDriver: false }),
    ]).start();
    bijToevoegenFavoriet();
  };

  const bepaalCijferKleur = (grade: string) => {
    if (!grade) return '#999';
    if (grade.startsWith('A')) return '#10B981';
    if (grade.startsWith('B')) return '#34D399';
    if (grade.startsWith('C')) return '#FBBF24';
    return '#F59E0B';
  };

  return (
    <TouchableOpacity style={stijlen.aanbevolenKaart} onPress={bijKlik} activeOpacity={0.85}>
      <View style={stijlen.aanbevolenPlaatjeBak}>
        {product.imageUrl && !imgError ? (
          // @ts-ignore
          <img 
            src={product.imageUrl} 
            alt={product.name}
            onError={() => setImgError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          />
        ) : (
          <Text style={stijlen.aanbevolenEmoji}>🧴</Text>
        )}
        <View style={[stijlen.aanbevolenCijfer, { backgroundColor: bepaalCijferKleur(product.grade) }]}>
          <Text style={stijlen.aanbevolenCijferTekst}>{product.grade}</Text>
        </View>
      </View>
      <View style={stijlen.aanbevolenInfo}>
        <Text style={stijlen.aanbevolenNaam} numberOfLines={2}>{product.name}</Text>
        <Text style={stijlen.aanbevolenMerk}>{product.brand}</Text>
      </View>
      <Animated.View style={{ transform: [{ scale: schaalAnimatie }] }}>
        <TouchableOpacity 
          style={[stijlen.voegFavorietKnop, isFavoriet && stijlen.voegFavorietKnopActief]}
          onPress={behandelToevoegKlik}
        >
          <Text style={stijlen.voegFavorietIcoon}>{isFavoriet ? '✓' : '+'}</Text>
        </TouchableOpacity>
      </Animated.View>
    </TouchableOpacity>
  );
};

interface FavorietKaartWebProps {
  item: ProductDisplay;
  onPress: () => void;
  onRemove: () => void;
}

const FavorietKaartWeb: React.FC<FavorietKaartWebProps> = ({ item, onPress, onRemove }) => {
  const [imgError, setImgError] = useState(false);

  const bepaalCijferKleur = (grade: string) => {
    if (!grade) return '#999';
    if (grade.startsWith('A')) return '#10B981';
    if (grade.startsWith('B')) return '#34D399';
    if (grade.startsWith('C')) return '#FBBF24';
    if (grade.startsWith('D')) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <TouchableOpacity
      style={stijlen.favorietKaart}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={stijlen.favorietPlaatjeBak}>
        {item.imageUrl && !imgError ? (
          // @ts-ignore
          <img 
            src={item.imageUrl} 
            alt={item.name}
            onError={() => setImgError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          />
        ) : (
          <Text style={{ fontSize: 36 }}>🧴</Text>
        )}
      </View>
      <View style={stijlen.favorietInhoud}>
        <View style={stijlen.favorietInfo}>
          <Text style={stijlen.favorietNaam} numberOfLines={2}>{item.name}</Text>
          <Text style={stijlen.favorietMerk}>{item.brand}</Text>
        </View>
        <View style={stijlen.favorietVoet}>
          <View style={[stijlen.favorietBadge, { backgroundColor: bepaalCijferKleur(item.grade) }]}>
            <Text style={stijlen.favorietBadgeTekst}>{item.grade}</Text>
          </View>
          <TouchableOpacity 
            style={stijlen.verwijderKnop}
            onPress={onRemove}
          >
            <Text style={stijlen.verwijderIcoon}>🗑️</Text>
          </TouchableOpacity>
        </View>
      </View>
    </TouchableOpacity>
  );
};

/* -------------------------------------------------------------------------- */
/*                              HOOFD COMPONENT                               */
/* -------------------------------------------------------------------------- */

export const FavoritesScreen: React.FC<any> = ({ navigation }) => {
  const { t } = useTranslation();
  const [favorieten, setFavorieten] = useState<ProductDisplay[]>([]);
  const [aanraders, setAanraders] = useState<ProductDisplay[]>([]);
  const [laden, setLaden] = useState(true);
  const [verversen, setVerversen] = useState(false);
  const scrollAnimatie = useRef(new Animated.Value(0)).current;

  // Autoscroll voor de aanraders (beweegt vanzelf).
  // Is hip tegenwoordig, zo'n oneindige loop.
  // Ziet eruit alsof er heel veel te zien is.
  useEffect(() => {
    const startAutoScroll = () => {
      Animated.loop(
        Animated.sequence([
          Animated.timing(scrollAnimatie, {
            toValue: 1,
            duration: 20000,
            easing: Easing.linear,
            useNativeDriver: false,
          }),
          Animated.timing(scrollAnimatie, {
            toValue: 0,
            duration: 0,
            useNativeDriver: false,
          }),
        ])
      ).start();
    };
    
    startAutoScroll();
  }, []);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      laadGegevens();
    });
    laadGegevens();
    return unsubscribe;
  }, [navigation]);

  const laadGegevens = async () => {
    try {
      setLaden(true);
      
      // Favorieten ophalen
      const favorietenData = await favoritesService.getFavorites();
      setFavorieten(favorietenData);

      // Aanraders ophalen (alles met A of B is goed genoeg).
      // We willen alleen de toppers laten zien hier.
      const alleProducten = await productService.getDiscoveryProducts(20);
      const goedeProducten = alleProducten.filter((p: ProductDisplay) => 
        p.grade && (p.grade.startsWith('A') || p.grade.startsWith('B'))
      );
      setAanraders(goedeProducten.slice(0, 15));
    } catch (fout) {
      console.error('Fout bij laden data:', fout);
      // Demo data voor als het stuk is (fallback)
      setAanraders([
        { id: 1, name: 'Ocean Breeze Shampoo', brand: 'EcoLife', grade: 'A+', category: 'Hair' } as ProductDisplay,
        { id: 2, name: 'Coral Safe Sunscreen', brand: 'PureBase', grade: 'A', category: 'Sun' } as ProductDisplay,
        { id: 3, name: 'Sea Kelp Conditioner', brand: 'GreenCo', grade: 'A-', category: 'Hair' } as ProductDisplay,
        { id: 4, name: 'Reef Guard Lotion', brand: 'BioLove', grade: 'B+', category: 'Body' } as ProductDisplay,
        { id: 5, name: 'Marine Body Wash', brand: 'EarthPly', grade: 'A', category: 'Body' } as ProductDisplay,
        { id: 6, name: 'Wave Rider Gel', brand: 'Solaris', grade: 'B+', category: 'Hair' } as ProductDisplay,
      ]);
    } finally {
      setLaden(false);
    }
  };

  const bijVerversen = useCallback(async () => {
    setVerversen(true);
    await laadGegevens();
    setVerversen(false);
  }, []);

  const behandelVerwijderFavoriet = async (productId: number) => {
    try {
      setFavorieten(vorige => vorige.filter(p => p.id !== productId));
      await favoritesService.removeFavorite(productId.toString());
    } catch (fout) {
      console.error('Fout bij verwijderen favoriet:', fout);
      await laadGegevens();
    }
  };

  const behandelToevoegFavoriet = async (product: ProductDisplay) => {
    try {
      const isFav = favorieten.some(f => f.id === product.id);
      if (isFav) {
        await behandelVerwijderFavoriet(product.id);
      } else {
        setFavorieten(vorige => [...vorige, product]);
        await favoritesService.addFavorite(product.id.toString());
      }
    } catch (fout) {
      console.error('Fout bij wisselen favoriet:', fout);
    }
  };

  const behandelProductKlik = (productId: number) => {
    navigation.navigate('ProductDetail', { productId });
  };

  const bepaalCijferKleur = (grade: string) => {
    if (!grade) return '#999';
    if (grade.startsWith('A')) return '#10B981';
    if (grade.startsWith('B')) return '#34D399';
    if (grade.startsWith('C')) return '#FBBF24';
    if (grade.startsWith('D')) return '#F59E0B';
    return '#EF4444';
  };

  const toonFavorietKaart = ({ item }: { item: ProductDisplay }) => (
    <FavorietKaartWeb
      item={item}
      onPress={() => behandelProductKlik(item.id)}
      onRemove={() => behandelVerwijderFavoriet(item.id)}
    />
  );

  const toonLegeFavorieten = () => (
    <View style={stijlen.legeFavorieten}>
      <View style={stijlen.leegIcoonBak}>
        <Text style={stijlen.leegIcoon}>💚</Text>
      </View>
      <Text style={stijlen.leegTitel}>{t('favorites.empty') || 'Nog geen favorieten'}</Text>
      <Text style={stijlen.leegSubtitel}>
        {t('favorites.emptyDescription') || 'Producten die je bewaart verschijnen hier'}
      </Text>
      <TouchableOpacity
        style={stijlen.browseKnop}
        onPress={() => navigation.navigate('SearchTab', { screen: 'Search' })}
      >
        <Text style={stijlen.browseKnopTekst}>Producten Bekijken</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={stijlen.container}>
      {/* De Header */}
      <View style={stijlen.kop}>
        <View style={stijlen.kopGradient}>
          <Text style={stijlen.kopTitel}>⭐ Favorieten</Text>
          <Text style={stijlen.kopSubtitel}>Jouw eco-vriendelijke collectie</Text>
        </View>
        <View style={stijlen.kopGolf} />
      </View>

      <ScrollView 
        contentContainerStyle={stijlen.scrollInhoud}
        showsVerticalScrollIndicator={false}
      >
        <View style={stijlen.inhoud}>
          {/* Aanraders Sectie */}
          <View style={stijlen.aanbevolenSectie}>
            <View style={stijlen.sectieKop}>
              <View>
                <Text style={stijlen.sectieLabel}>SPECIAAL VOOR JOU</Text>
                <Text style={stijlen.sectieTitel}>🌿 Top Beoordeelde Producten</Text>
              </View>
              <View style={stijlen.ecoLabel}>
                <Text style={stijlen.ecoLabelTekst}>A & B Cijfers</Text>
              </View>
            </View>
            
            <Text style={stijlen.sectieSubtitel}>
              Scroll om eco-producten te ontdekken. Tik op + om op te slaan!
            </Text>

            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              style={stijlen.aanbevolenScroll}
              contentContainerStyle={stijlen.aanbevolenScrollInhoud}
            >
              {aanraders.map((product, index) => (
                <AanbevolenKaart
                  key={`${product.id}-${index}`}
                  product={product}
                  bijKlik={() => behandelProductKlik(product.id)}
                  bijToevoegenFavoriet={() => behandelToevoegFavoriet(product)}
                  isFavoriet={favorieten.some(f => f.id === product.id)}
                />
              ))}
              
              {/* Ontdek meer knopje */}
              <TouchableOpacity 
                style={stijlen.ontdekMeerKaart}
                onPress={() => navigation.navigate('SearchTab', { screen: 'Search' })}
              >
                <Text style={stijlen.ontdekMeerIcoon}>🔍</Text>
                <Text style={stijlen.ontdekMeerTekst}>Ontdek{'\n'}Meer</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>

          {/* Mijn Favorieten */}
          <View style={stijlen.favorietenSectie}>
            <View style={stijlen.sectieKop}>
              <Text style={stijlen.sectieTitel}>❤️ Mijn Favorieten</Text>
              <Text style={stijlen.favorietAantal}>{favorieten.length} items</Text>
            </View>

            {favorieten.length > 0 ? (
              <FlatList
                data={favorieten}
                renderItem={toonFavorietKaart}
                keyExtractor={item => item.id.toString()}
                scrollEnabled={false}
                contentContainerStyle={stijlen.favorietenLijstInhoud}
              />
            ) : (
              toonLegeFavorieten()
            )}
          </View>

          {/* Statistiekjes onderaan */}
          <View style={stijlen.statsVoet}>
            <View style={stijlen.statItem}>
              <Text style={stijlen.statWaarde}>{favorieten.length}</Text>
              <Text style={stijlen.statLabel}>Opgeslagen</Text>
            </View>
            <View style={stijlen.statVerdeler} />
            <View style={stijlen.statItem}>
              <Text style={stijlen.statWaarde}>{aanraders.length}</Text>
              <Text style={stijlen.statLabel}>Aanbevolen</Text>
            </View>
            <View style={stijlen.statVerdeler} />
            <View style={stijlen.statItem}>
              <Text style={stijlen.statWaarde}>🌊</Text>
              <Text style={stijlen.statLabel}>Oceaan Veilig</Text>
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const stijlen = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    minHeight: '100%' as any,
  },
  kop: {
    position: 'relative',
    height: 130,
    overflow: 'hidden',
  },
  kopGradient: {
    flex: 1,
    // @ts-ignore
    backgroundImage: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 20,
  },
  kopTitel: {
    fontSize: 24,
    fontWeight: '700',
    color: 'white',
  },
  kopSubtitel: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.9)',
    marginTop: 4,
  },
  kopGolf: {
    position: 'absolute',
    bottom: -1,
    left: 0,
    right: 0,
    height: 30,
    backgroundColor: '#F8FAFC',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
  },
  scrollInhoud: {
    flexGrow: 1,
    paddingBottom: 100,
  },
  inhoud: {
    paddingHorizontal: 20,
    maxWidth: 800,
    width: '100%',
    alignSelf: 'center',
  },
  aanbevolenSectie: {
    marginBottom: 32,
  },
  sectieKop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  sectieLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#10B981',
    letterSpacing: 1,
    marginBottom: 4,
  },
  sectieTitel: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1E293B',
  },
  sectieSubtitel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 16,
  },
  ecoLabel: {
    backgroundColor: '#D1FAE5',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  ecoLabelTekst: {
    fontSize: 12,
    fontWeight: '600',
    color: '#059669',
  },
  aanbevolenScroll: {
    marginHorizontal: -20,
  },
  aanbevolenScrollInhoud: {
    paddingHorizontal: 20,
    gap: 14,
  },
  aanbevolenKaart: {
    width: 160,
    backgroundColor: 'white',
    borderRadius: 20,
    overflow: 'hidden',
    // @ts-ignore
    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
    marginRight: 14,
  },
  aanbevolenPlaatjeBak: {
    height: 100,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  aanbevolenEmoji: {
    fontSize: 40,
  },
  aanbevolenCijfer: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 32,
    height: 32,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  aanbevolenCijferTekst: {
    color: 'white',
    fontSize: 12,
    fontWeight: '700',
  },
  aanbevolenInfo: {
    padding: 14,
    paddingBottom: 8,
  },
  aanbevolenNaam: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 4,
    minHeight: 36,
  },
  aanbevolenMerk: {
    fontSize: 12,
    color: '#64748B',
  },
  voegFavorietKnop: {
    margin: 14,
    marginTop: 0,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#EEF2FF',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#C7D2FE',
  },
  voegFavorietKnopActief: {
    backgroundColor: '#10B981',
    borderColor: '#10B981',
  },
  voegFavorietIcoon: {
    fontSize: 18,
    fontWeight: '700',
    color: '#6366F1',
  },
  ontdekMeerKaart: {
    width: 120,
    height: 200,
    backgroundColor: '#EEF2FF',
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#C7D2FE',
    borderStyle: 'dashed',
  },
  ontdekMeerIcoon: {
    fontSize: 32,
    marginBottom: 8,
  },
  ontdekMeerTekst: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6366F1',
    textAlign: 'center',
  },
  favorietenSectie: {
    marginBottom: 24,
  },
  favorietAantal: {
    fontSize: 14,
    color: '#64748B',
  },
  favorietenLijstInhoud: {
    gap: 12,
  },
  favorietKaart: {
    backgroundColor: 'white',
    borderRadius: 20,
    overflow: 'hidden',
    // @ts-ignore
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    marginBottom: 12,
  },
  favorietPlaatjeBak: {
    height: 120,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  favorietInhoud: {
    padding: 16,
  },
  favorietInfo: {
    marginBottom: 12,
  },
  favorietNaam: {
    fontSize: 17,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 4,
  },
  favorietMerk: {
    fontSize: 14,
    color: '#64748B',
  },
  favorietVoet: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  favorietBadge: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 12,
  },
  favorietBadgeTekst: {
    color: 'white',
    fontSize: 14,
    fontWeight: '700',
  },
  verwijderKnop: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#FEE2E2',
    justifyContent: 'center',
    alignItems: 'center',
  },
  verwijderIcoon: {
    fontSize: 20,
  },
  legeFavorieten: {
    alignItems: 'center',
    paddingVertical: 48,
  },
  leegIcoonBak: {
    width: 90,
    height: 90,
    borderRadius: 45,
    backgroundColor: '#FEE2E2',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  leegIcoon: {
    fontSize: 40,
  },
  leegTitel: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
  },
  leegSubtitel: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 24,
  },
  browseKnop: {
    // @ts-ignore
    backgroundImage: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 25,
    // @ts-ignore
    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.35)',
  },
  browseKnopTekst: {
    color: 'white',
    fontSize: 15,
    fontWeight: '600',
  },
  statsVoet: {
    flexDirection: 'row',
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 20,
    justifyContent: 'space-around',
    alignItems: 'center',
    // @ts-ignore
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  statItem: {
    alignItems: 'center',
  },
  statWaarde: {
    fontSize: 24,
    fontWeight: '800',
    color: '#1E293B',
  },
  statLabel: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
  },
  statVerdeler: {
    width: 1,
    height: 40,
    backgroundColor: '#E2E8F0',
  },
});
